import moment from "moment"
import React, { useEffect, useRef, useState } from "react"
import CurrencyInput from "react-currency-input-field"
import { Col, FormGroup, Input, Label, Row } from "reactstrap"
import api from "services/api.service"
import { userService } from "services/user.service"
import { currencyFormatter } from "utils/formatters"
import { getAlternativeYears } from "utils/alternativeHelper"
import { TableRenderer } from "../ContractSection"

const ContractSectionViewOnly = ({
  alternativeId,
  alternative,
  enterprise,
  scenario,
}) => {
  const currentUser = userService.getLoggedInUser()
  const [contractForecasts, setContractForecasts] = useState([])
  const [contractCompares, setContractCompares] = useState([])
  const [contractItems, setContractItems] = useState([])
  const [groupedContractItems, setGroupedContractItems] = useState([])
  const [strategyOptions, setStrategyOptions] = useState([])
  const [contractEntityOptions, setContractEntityOptions] = useState([])
  const [resourceFlowContracts, setResourceFlowContracts] = useState([])
  const [payments, setPayments] = useState([])
  const [comparisonByObligations, setComparisonByObligations] = useState(true)
  const [contractComparePayments, setContractComparePayments] = useState([])

  const [totalsByYear, setTotalsByYear] = useState(true)
  const [summarizeByPhase, setSummarizeByPhase] = useState(false)
  const [showAggregatedCostPool, setShowAggregatedCostPool] = useState(false)


  useEffect(() => {
    if (alternativeId != null && alternativeId > 0) {
      loadData()
    }
  }, [alternativeId])

//   useEffect(() => {
//     if (enterprise != undefined && enterprise != null) {
//       if (enterprise.obligationForm.toLowerCase() == "multi") {
//         setTotalsByYear(true)
//       } else {
//         setTotalsByYear(true)
//       }
//     }
//   }, [enterprise])

  const loadData = async () => {
    let forecast = await api.getContractForecast(alternativeId)
    setContractForecasts(forecast)

    let str = await api.getScenarioList(alternative.scenarioID, "Strategy")
    setStrategyOptions(str)
    let ce = await api.getContractEntities(alternative.scenarioID)
    setContractEntityOptions(ce)
    await loadContractItems()
    await loadResourceFlowContracts()
    await loadPayments()
  }

  const loadPayments = async () => {
    let p = await api.getPayments(alternativeId)
    setPayments(p)
  }

  const loadResourceFlowContracts = async () => {
    let rfc = await api.getResourceFlowContracts(
      currentUser.userID,
      alternativeId
    )
    setResourceFlowContracts(rfc)
  }

  const loadContractItems = async () => {
    let resourceGroupLists = []
    let items = await api.getContractItems(alternativeId)
    if (
      enterprise != null &&
      enterprise.obligationForm.toLowerCase() == "multi"
    ) {
      let uniqueResourceGroupIds = [
        ...new Set(items.map(x => x.resourceGroupID)),
      ]
      let tasks = uniqueResourceGroupIds.map(
        async rgId =>
          await api.getContractsForResource(alternative.scenarioID, rgId)
      )
      let contractsByResourceGroup = await Promise.all(tasks)
      uniqueResourceGroupIds.forEach((rgId, idx) => {
        resourceGroupLists.push({
          resourceGroupID: rgId,
          contracts:
            contractsByResourceGroup.length >= idx
              ? contractsByResourceGroup[idx]
              : [],
        })
      })

      items.forEach(item => {
        let rgl = resourceGroupLists.find(
          x => x.resourceGroupID == item.resourceGroupID
        )
        if (rgl != undefined) {
          item.contractOptions = rgl.contracts
        }
      })
    }

    setContractItems(items)
    let grouped = getAggregatedContractItems(items)
    console.log("Contract Items", grouped)
    setGroupedContractItems(grouped)
  }

  const getAggregatedContractItems = contractItems => {
    const groupedItems = {}

    contractItems.forEach(item => {
      let cpType = item.costPoolName?.startsWith("Pre-Project")
        ? "Pre-Project"
        : item.costPoolName?.startsWith("Project")
        ? "Project"
        : "Post-Project"
      const key = `${cpType}-${item.contractID}`
      if (!groupedItems[key]) {
        groupedItems[key] = {
          costPoolType: cpType,
          contractID: item.contractID,
          contractName:
            item.contractOptions?.find(x => x.contractID == item.contractID)
              ?.contractName || "",
          contractItemYears: JSON.parse(JSON.stringify(item.contractItemYears)), // [...item.contractItemYears],
        }
      } else {
        groupedItems[key].contractItemYears.forEach((year, index) => {
          year.amount += item.contractItemYears[index].amount
        })
      }
    })

    return Object.values(groupedItems)
  }

  const getRowTotal = contractItem => {
    return contractItem.contractItemYears.reduce(
      (acc, item) => acc + getNumberDefaultZero(item.amount),
      0
    )
  }

  const getNumberDefaultZero = num => {
    if (num == undefined || num == null || num == "") {
      return 0
    }
    return num
  }

  const getPaymentsTotal = year => {
    let total = 0

    if (year == "Total") {
      payments.forEach(p => {
        total += p.paymentAmount
      })
    } else {
      payments.forEach(p => {
        if (moment(p.paymentDate).year() == year) {
          total += p.paymentAmount
        }
      })
    }

    return currencyFormatter.format(total)
  }

  const alternativeYears = getAlternativeYears(alternative)

  return (
    <React.Fragment>
      <Row className="mb-3">
        {scenario?.costMultiplier == 1000 && (
          <Label style={{ marginBottom: "10px" }}>
            Enter Amounts in $000’s
          </Label>
        )}
        <Col className="d-flex-row ai-center gap-3">
          <FormGroup switch>
            <Input
              type="switch"
              checked={totalsByYear}
              style={{
                backgroundColor: totalsByYear ? "green" : "transparent",
              }}
              disabled={true}
            />
            <Label check style={{ marginLeft: "10px" }}>
              Total by Year
            </Label>
          </FormGroup>
          <FormGroup switch>
            <Input
              type="switch"
              checked={summarizeByPhase}
              style={{
                backgroundColor: summarizeByPhase ? "green" : "transparent",
              }}
              disabled={true}
            />
            <Label check style={{ marginLeft: "10px" }}>
              Summarize by Phase
            </Label>
          </FormGroup>
        </Col>
      </Row>
      {contractForecasts.length > 0 && (
        <Row>
          <Col>
            <h5>
              <b>
                {enterprise?.contractLabel ?? "Contract"} $ from Forecast Tab
              </b>
            </h5>
            <TableRenderer
              tableItems={contractForecasts}
              showYears={totalsByYear}
              shouldColorZeroValues={true}
              summarizeByPhase={summarizeByPhase}
            />
          </Col>
        </Row>
      )}
      {contractCompares.length > 0 &&
        enterprise.obligationForm.toLowerCase() == "multi" && (
          <Row>
            <Col>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <h5 style={{ margin: 0 }}>
                  <b>
                    {enterprise?.contractLabel ?? "Contract"} $ Remaining to be
                    Assigned
                  </b>
                </h5>
                {enterprise.obligationForm.toLowerCase() !== "multi" && (
                  <div>
                    <input
                      style={{ marginLeft: "10px", cursor: "pointer" }}
                      type="radio"
                      id="radio1"
                      name="radio1"
                      checked={comparisonByObligations == true}
                      disabled={true}
                    />
                    <label
                      style={{ marginLeft: "10px", cursor: "pointer" }}
                      className="me-1"
                      htmlFor="radio1"
                    >
                      {enterprise.obligationLabel}
                    </label>
                    <input
                      style={{ marginLeft: "10px", cursor: "pointer" }}
                      type="radio"
                      id="radio2"
                      name="radio2"
                      checked={comparisonByObligations == false}
                      disabled={true}
                    />
                    <label
                      style={{ marginLeft: "10px", cursor: "pointer" }}
                      className="me-1"
                      htmlFor="radio2"
                    >
                      {enterprise != null && enterprise.paymentLabel + "s"}
                    </label>
                  </div>
                )}
              </div>

              {comparisonByObligations && (
                <TableRenderer
                  tableItems={contractCompares}
                  shouldColorZeroValues={true}
                  showYears={totalsByYear}
                  summarizeByPhase={summarizeByPhase}
                />
              )}

              {!comparisonByObligations && (
                <TableRenderer
                  tableItems={contractComparePayments}
                  showYears={totalsByYear}
                  shouldColorZeroValues={true}
                  summarizeByPhase={summarizeByPhase}
                />
              )}
            </Col>
          </Row>
        )}

      {enterprise != null &&
        enterprise.obligationForm.toLowerCase() == "multi" &&
        contractItems.length > 0 && (
          <Row>
            <Col>
              <div className="d-flex-row ai-center gap-3 mb-2">
                <h5 style={{ margin: 0 }}>
                  <b>
                    {enterprise != null &&
                      enterprise.obligationLabel + " $ Assigned"}
                  </b>
                </h5>
                <FormGroup switch>
                  <Input
                    type="switch"
                    checked={showAggregatedCostPool}
                    style={{
                      backgroundColor: showAggregatedCostPool
                        ? "green"
                        : "transparent",
                    }}
                    disabled={true}
                  />
                  <Label check style={{ marginLeft: "10px" }}>
                    {showAggregatedCostPool
                      ? "Detail"
                      : `Phase and ${
                          enterprise?.obligationLabel ?? "funding source"
                        }`}
                  </Label>
                </FormGroup>
              </div>

              <table
                className="table table-bordered lower-padding-table"
                style={{ backgroundColor: "white" }}
              >
                <thead>
                  <tr>
                    <th></th>
                    <th style={{ width: "12%" }}>
                      Phase <br /> Cost Pool
                    </th>
                    <th style={{ width: "15%" }}>
                      {enterprise?.contractLabel ?? "Contract"} <br />{" "}
                      Acquisition Approach
                    </th>
                    <th style={{ width: "15%" }}>Entity Type - Name</th>
                    <th>
                      PoP Start <br /> PoP End
                    </th>
                    {totalsByYear &&
                      alternativeYears.map((y, idx) => (
                        <th key={idx}>FY {y}</th>
                      ))}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {contractItems.map((c, idx) => {
                    return (
                      <tr key={idx}>
                        <td></td>
                        <td>{c.costPoolName}</td>
                        <td
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          }}
                        >
                          {c.contractOptions !== undefined && (
                            <p>
                              {
                                c.contractOptions.find(
                                  x => x.contractID == c.contractID
                                )?.contractName
                              }
                            </p>
                          )}
                          <p>
                            {
                              strategyOptions.find(
                                x => x.listItemID == c.strategyID
                              )?.listItemName
                            }
                          </p>
                        </td>
                        <td>
                          <p>
                            {
                              contractEntityOptions.find(
                                x => x.contractEntityID == c.contractEntityID
                              )?.contractEntityType
                            }
                          </p>
                        </td>

                        <td
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "2px",
                          }}
                        >
                          <Input
                            name="startDate"
                            type="date"
                            style={{ padding: "0px 0px" }}
                            disabled={true}
                            value={moment(c.startDate).format("YYYY-MM-DD")}
                          />
                          {/* <br /> */}
                          <Input
                            name="endDate"
                            type="date"
                            style={{ padding: "0px 0px" }}
                            disabled={true}
                            value={moment(c.endDate).format("YYYY-MM-DD")}
                          />
                        </td>
                        {totalsByYear &&
                          c.contractItemYears.map((year, idx) => (
                            <td key={idx} className="align-end">
                              <CurrencyInput
                                className="form-control align-end"
                                style={{ padding: "0px 0px" }}
                                value={year.amount}
                                disabled={true}
                                prefix={"$"}
                              />
                            </td>
                          ))}

                        <td className="align-end">
                          {currencyFormatter.format(getRowTotal(c))}
                        </td>
                      </tr>
                    )
                  })}
                  <tr
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "lightgray",
                    }}
                  >
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    {totalsByYear &&
                      alternativeYears.map((y, idx) => (
                        <td key={idx} className="align-end">
                          {currencyFormatter.format(
                            contractItems.reduce(
                              (acc, item) =>
                                acc +
                                (item.contractItemYears.find(
                                  cy => cy.yearID == y
                                )?.amount || 0),
                              0
                            )
                          )}
                        </td>
                      ))}
                    <td className="align-end">
                      {currencyFormatter.format(
                        contractItems.reduce(
                          (acc, item) => acc + getRowTotal(item),
                          0
                        )
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        )}
      {(enterprise == null ||
        enterprise.obligationForm.toLowerCase() !== "multi") &&
        contractItems.length > 0 && (
          <Row>
            <Col>
              <h5>
                <b>
                  {enterprise != null &&
                    enterprise.obligationLabel + " $ Assigned"}
                </b>
              </h5>
              <table
                className="table table-bordered lower-padding-table"
                style={{ backgroundColor: "white" }}
              >
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Contract</th>
                    <th>Approval Date</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Vendor</th>
                    <th>Notes</th>
                    <th>Status</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {contractItems.map((c, idx) => {
                    return (
                      <tr key={idx}>
                        <td>{c.contractItemNum}</td>
                        <td>{c.contractName}</td>
                        <td>
                          {c.approvalDate == null
                            ? ""
                            : moment(c.approvalDate).format("MM/DD/YYYY")}
                        </td>
                        <td>
                          {c.startDate == null
                            ? ""
                            : moment(c.startDate).format("MM/DD/YYYY")}
                        </td>
                        <td>
                          {c.endDate == null
                            ? ""
                            : moment(c.endDate).format("MM/DD/YYYY")}
                        </td>
                        <td>{c.vendor}</td>
                        <td>{c.comments}</td>
                        <td>{c.contractItemStatus}</td>
                        <td className="align-end">
                          {c.totalAmount &&
                            currencyFormatter.format(c.totalAmount)}
                        </td>
                      </tr>
                    )
                  })}
                  <tr
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "lightgray",
                    }}
                  >
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td className="align-end">
                      {currencyFormatter.format(
                        contractItems.reduce(
                          (acc, item) => acc + (item.totalAmount || 0),
                          0
                        )
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        )}

      {enterprise != null &&
        enterprise.paymentLabel != "" &&
        payments.length > 0 && (
          <Row>
            <Col>
              <h5>
                <b>{enterprise.paymentLabel + "s"}</b>
              </h5>
              <table
                className="table table-bordered lower-padding-table"
                style={{ backgroundColor: "white" }}
              >
                {totalsByYear ? (
                  <thead>
                    <tr>
                      <th> </th>
                      <th>{enterprise != null && enterprise.paymentLabel} #</th>
                      <th>
                        {enterprise != null && enterprise.obligationLabel}{" "}
                      </th>
                      <th>Date</th>
                      <th>PO</th>
                      <th>Notes</th>
                      {alternativeYears.map((y, idx) => (
                        <th key={idx}>FY {y}</th>
                      ))}

                      <th>Total</th>
                    </tr>
                  </thead>
                ) : (
                  <thead>
                    <tr>
                      <th></th>
                      <th>{enterprise != null && enterprise.paymentLabel} #</th>
                      <th></th>
                      <th>Date</th>
                      <th>PO</th>
                      <th>Notes</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                )}

                {totalsByYear ? (
                  <tbody>
                    {payments.map((p, idx) => (
                      <tr key={idx}>
                        <td></td>
                        <td>{p.paymentNumber}</td>
                        <td>{p.contractItemName}</td>
                        <td>{moment(p.paymentDate).format("MM/DD/YYYY")}</td>
                        <td>{p.poNumber}</td>
                        <td>{p.notes}</td>
                        {alternativeYears.map((y, idx) => (
                          <td key={idx} className="align-end">
                            {moment(p.paymentDate).year() == y &&
                              currencyFormatter.format(p.paymentAmount)}
                          </td>
                        ))}

                        <td className="align-end">
                          {currencyFormatter.format(p.paymentAmount)}
                        </td>
                      </tr>
                    ))}
                    {payments.length > 0 && (
                      <tr
                        style={{
                          fontWeight: "bold",
                          backgroundColor: "lightgray",
                        }}
                      >
                        <td></td>
                        <td>
                          <b>Totals</b>
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        {alternativeYears.map((y, idx) => (
                          <td key={idx} className="align-end">
                            {getPaymentsTotal(y)}
                          </td>
                        ))}
                        <td className="align-end">
                          {getPaymentsTotal("Total")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                ) : (
                  <tbody>
                    {payments.map((p, idx) => (
                      <tr key={idx}>
                        <td>{p.paymentNumber}</td>
                        <td>{moment(p.paymentDate).format("MM/DD/YYYY")}</td>
                        <td>{p.poNumber}</td>
                        <td>{p.notes}</td>
                        <td>{currencyFormatter.format(p.paymentAmount)}</td>
                      </tr>
                    ))}
                    {payments.length > 0 && (
                      <tr
                        style={{
                          fontWeight: "bold",
                          backgroundColor: "lightgray",
                        }}
                      >
                        <td>
                          <b>Totals</b>
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="align-end">
                          {getPaymentsTotal("Total")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
            </Col>
          </Row>
        )}

    </React.Fragment>
  )
}

export default ContractSectionViewOnly
